// Adjust balance — spec §6.5. The audit door: a correction written straight
// to the books, with its reason word for word. No payment_method (this is an
// internal ledger correction, not a real-world flow).
//
// Sweep of 14 Sept 2026 (SWEEP-SCOPE §5.12 + item 35):
//   · NO DEFAULT DIRECTION. It defaulted to "They owe more" with the toggle
//     BELOW the amount, so a correction typed top to bottom raised what the
//     party owed. Now the direction starts unpicked, the button is off and
//     reads "Pick which way first", and the toggle sits above the amount:
//     who → which way → how much → why.
//   · THE ACCOUNT DECIDES THE ASSET in desk-account mode (RailPicker returns
//     slug and asset together), so changing an asset can never silently
//     clear a chosen account — the fault the old asset-select + slug-combobox
//     pair had. In party mode the AssetPicker sits before the amount.
//   · ScreenHeader on both devices, "Desk account" (never rail), "Search
//     party", the box in the standard before → after words, the success line
//     with the reference. The posting body is as it was.

import { useState, useMemo, useRef } from 'react';
import { errorWords, readWords } from '../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, treasuryPath, customerPath, counterpartyPath, makeReference } from '../lib/workflow';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { AssetPicker } from '../components/AssetPicker';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field, FieldRow } from '../components/Field';
import { BoxLines, boxWords } from '../components/BoxLines';
import { ErrorLine } from '../components/ErrorLine';
import { AccountsDown, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { amountOk, amountProblem } from '../lib/amount';
import { partyName, partySubtitle, isStale, needsNameReview, foldAsset, railName, sameAsset, rankForQuery, partyBalanceOf } from '../lib/naming';
import { displayAsset, fmtAmount } from '../lib/format';
import { readState } from '../lib/read-state';

type Who = 'treasury' | 'customer';
type Way = 'writeup' | 'writedown';

/** What the last posting said — the reference that landed, whether the BFF deduped it, and the box as it stands after. */
export interface Posted { reference: string; deduped: boolean; who: Who; name: string; asset: string; after: number | null }

/** The answer to a correction: a headline (design system §3: one line) and, under it in muted type, what the box now
 *  reads — the amount and its unit one unbreakable value. A deduped answer says so instead: the ledger already held
 *  this reference and nothing moved on this request. */
export function PostedLine({ posted }: { posted: Posted }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  const unit = displayAsset(posted.asset);
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={posted.reference} />}>✓ Adjusted</StateLine>
      <p className="text-meta text-tg-hint">
        {posted.after === null
          ? `${posted.name} · ${unit}`
          : posted.who === 'treasury'
            ? `${posted.name} now holds ${fmtAmount(posted.after, posted.asset)}\u00a0${unit}`
            : `${posted.name} now ${boxWords(posted.after, posted.asset) === 'square' ? 'square' : `${boxWords(posted.after, posted.asset)}\u00a0${unit}`}`}
      </p>
    </div>
  );
}

export function AdjustBalance() {
  const nav = useNavigate();

  // ?party=<slug> from a party page; ?rail=<slug>&railAsset=<ASSET> from an
  // own-account page's "Adjust this account" door (the contract Record money
  // already honours) — that opens in desk-account mode with the account set.
  const [search] = useSearchParams();
  const railParam = search.get('rail'), railAssetParam = search.get('railAsset');
  const [who, setWho] = useState<Who>(railParam ? 'treasury' : 'customer');
  // Party mode: the party and the asset, picked apart (a box holds many).
  const [slug, setSlug] = useState(search.get('party') ?? '');
  const [partyAsset, setPartyAsset] = useState<string>('AED');
  // Desk-account mode: one pick fixes both the account and its asset.
  const [rail, setRail] = useState<RailValue>(railParam ? { slug: railParam, asset: railAssetParam ?? '' } : { slug: '', asset: '' });
  // Which way — unpicked until the operator says (item 35).
  const [direction, setDirection] = useState<Way | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);

  const asset = who === 'treasury' ? rail.asset : partyAsset;

  // Desk accounts, with balances — the same key every money screen shares.
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', 'dubaiotc'],
    queryFn:  () => api.treasuryBalances('dubaiotc'),
    enabled:  who === 'treasury',
    staleTime: 30_000,
  });
  const rows = treasury.data?.rows ?? [];
  // What each read is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED; 'waiting' is a first read, the
  // retry of a failure or an offline pause; 'failed' is an error (a failed refresh keeps its old data beside it). Nothing is drawn from the last two.
  const accounts = readState(treasury);
  const railRow = useMemo(
    () => rows.find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset)),
    [rows, rail.slug, rail.asset],
  );

  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q }),
    enabled: who === 'customer' && slug.length > 0,
  });
  const lookup = readState(parties);
  // "Party" here means a customer OR a merchant — anything but own_account.
  // Own accounts are picked through the Desk account toggle, not this box.
  // Ranked by what was typed, then cut — never cut first: the search asks the books for the three-character prefix, so a party that
  // matches the whole text can sit beyond row 20 of that answer (rankForQuery puts the best matches first, lib/naming.ts).
  const partyOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery((parties.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'), slug)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, slug]);

  // ── The box as it stands ────────────────────────────────────────────────
  const picked = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === slug),
    [parties.data, slug],
  );
  const partyExists = !!picked;
  const partyBalances = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn:  () => api.partyDetail(slug),
    enabled:  who === 'customer' && partyExists,
    staleTime: 30_000,
  });
  const box = readState(partyBalances);
  // NO CLAIM FROM A READ THAT FAILED (22 Oct 2026 review): a failed refresh keeps the query's old data, so a cached AED
  // zero still drew "AED · square" and the projected balance used the stale figure beside "Couldn't read the balance".
  // Until a read has SUCCEEDED there is no current balance and no projected one — only the line that says why (failed or still reading).
  const current: number | null = who === 'treasury'
    ? (rail.slug && accounts === 'known' ? (railRow?.balance ?? 0) : null)
    : (partyExists && box === 'known'
        ? partyBalanceOf(partyBalances.data?.balances, partyAsset)
        : null);

  // WHAT WAS TYPED, CHECKED BEFORE IT IS SENT. The gate here was `!amount`, so
  // an Indian-grouped '1,000' left as `"amount": null` and came back as "The
  // books hit a fault: toMinor: not a plain decimal string" — a 500 telling the
  // operator to contact support about a comma. One rule for every amount box
  // (lib/amount.ts); a correction is the last place to guess at a number.
  const amountWords = amountProblem(amount);
  const amountN = Number(amount) || 0;
  // "More" sends the offset to the target, so the balance rises; "less" the
  // reverse — for a party's box and a desk account alike.
  const next = current !== null && direction ? current + (direction === 'writeup' ? amountN : -amountN) : null;

  const whoName = who === 'treasury'
    ? railName(railRow ?? { slug: rail.slug })
    : (picked ? partyName(picked) : slug);
  const targetPicked = who === 'treasury' ? !!rail.slug && !!rail.asset : !!slug;

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — the
  // fix Record money got on 5 Sept 2026, arriving here on 14 Sept (sweep item
  // 30). It was minted inside mutationFn, which runs on EVERY submit: a
  // timeout after the BFF had posted, Adjust pressed again, and the second
  // attempt carried a fresh ADJ-… the ledger could not recognise as the same
  // correction, so it posted twice — on the one screen that writes straight
  // to the books. Held in a ref, not state: it must not re-render, and it
  // must not reset when a field is edited mid-retry. Minted on the first
  // submit, cleared on success. No prefix or day rides on this screen's
  // fields, so nothing here retires it early.
  const refRef = useRef<string | null>(null);
  // What the last posting said — the reference that landed, whether the BFF
  // deduped it (a retry whose first attempt had landed: nothing moved on THIS
  // request), and the box as it stands after it, for the success line.
  const [posted, setPosted] = useState<Posted | null>(null);
  const submit = useMutation({
    mutationFn: async () => {
      if (!direction) throw new Error('pick which way first');
      // A merchant's position lives on counterparty:{e}:{slug}, a customer's on
      // customer:{e}:{slug}. This built customerPath() for BOTH while the picker
      // above deliberately offers merchants (it filters only own_account), so a
      // merchant correction aimed at an account holding nothing and silently
      // split their position — the 2026-08-22 failure, in a screen that had not
      // been fixed. The BFF now corrects this server-side too (adjustments.ts);
      // both, because a client-side rule alone is only as good as the open tab.
      // THE PARTY MUST HAVE BEEN READ: its type picks the account (below); unread, a merchant would be adjusted as a customer.
      if (who === 'customer' && (!picked || lookup !== 'known')) throw new Error('The party has not been read — wait for the list, or pick one from it.');
      const partyPath = picked?.party_type === 'counterparty' ? counterpartyPath : customerPath;
      const target = who === 'treasury' ? treasuryPath(rail.slug, rail.asset) : partyPath(slug);
      const offset = '@adjustments:writeup';
      const [source, destination] = direction === 'writeup' ? [offset, target] : [target, offset];
      refRef.current ??= makeReference('ADJ');
      const reference = refRef.current;
      const data = await api.adjust({
        source, destination,
        amount: { asset, amount: Number(amount) },
        reason,
        reference,
      });
      return { data, reference, who, name: whoName, asset, after: next };
    },
    onSuccess: ({ data, reference, ...facts }) => {
      refRef.current = null;
      setPosted({ reference, deduped: !!data.deduped, ...facts });
      // The figures leave with the entry (§7 rule 12), and the direction goes
      // back to unpicked — the next correction says its own way.
      setAmount(''); setReason(''); setConfirm(false); setDirection(null);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError:   () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  // The one toggle (styles.css, emphasis rule): the picked side is lit.
  const segmented = (active: boolean) => `seg ${active ? 'seg-on' : ''}`;
  // A READ THAT FAILED IS NOT AN EMPTY ONE: no accounts, no party, no balance said nothing before — and a picker with
  // nothing in it reads as "none exist". One line, what failed and the way out; the books' own sentence behind a tap.
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;

  const WAYS: ReadonlyArray<{ v: Way; party: string; account: string }> = [
    // PLAIN WORDS, NOT DEBIT AND CREDIT. A party's box is positive when the
    // party owes the desk, so "+ credit" raised what they owed — the exact
    // opposite of what the accounting word suggests (3 Sept audit). Say what
    // the posting does to the thing on screen.
    { v: 'writeup',   party: 'They owe more', account: 'More on the account' },
    { v: 'writedown', party: 'They owe less', account: 'Less on the account' },
  ];

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      <ScreenHeader
        title="Adjust balance"
        subtitle="A correction written straight to the books, with its reason."
        help={
          <>
            <p>
              The audit door. For a figure the books cannot fix any other way — dust, a wire shortfall,
              a balance that is wrong and cannot be reversed. Every adjustment carries its reason word for
              word and shows in Money movements.
            </p>
            <p>
              A wrong entry that can be reversed is reversed from Money movements; a trade booked the
              wrong way round is voided from its deal and booked again. Neither is an adjustment.
            </p>
          </>
        }
      />

      {/* WHO — a desk account or a party. */}
      <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
        {([
          { v: 'treasury', l: 'Desk account' },
          { v: 'customer', l: 'Party' },        // customer OR merchant — anything but a desk account
        ] as const).map((t) => (
          <button key={t.v} type="button" onClick={() => { setWho(t.v); setDirection(null); }} className={segmented(who === t.v)}>
            {t.l}
          </button>
        ))}
      </div>

      {who === 'treasury' && accounts === 'failed' && (
        <StateLine tone="warn" more={readWords(treasury.error)} action={retry(treasury.refetch)}>
          Couldn&rsquo;t read the accounts — try again.
        </StateLine>
      )}
      {who === 'customer' && lookup === 'failed' && (
        <StateLine tone="warn" more={readWords(parties.error)} action={retry(parties.refetch)}>
          Couldn&rsquo;t read parties — try again.
        </StateLine>
      )}
      {who === 'customer' && partyExists && box === 'failed' && (
        <StateLine tone="warn" more={readWords(partyBalances.error)} action={retry(partyBalances.refetch)}>
          Couldn&rsquo;t read the balance — try again.
        </StateLine>
      )}

      <FieldRow>
        {who === 'treasury' ? (
          <Field label="Desk account">
            {/* One pick fixes the account AND its asset (item 35): the asset
                can never be changed underneath a chosen account. */}
            {/* 21 Oct 2026 audit: the balance was on screen twice — in the picker
                ("AED 741,039.25") and under it ("holds 741,039.25 → holds
                739,789.25"). The line under the field is the one that shows what
                this correction does, so it stays; the picker keeps the name. */}
            {/* No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party. */}
            {accounts === 'failed' && !treasury.data ? <AccountsDown value={rail} /> : (
              <RailPicker
                value={rail}
                onChange={setRail}
                rows={rows}
                loading={treasury.isFetching || accounts === 'waiting'}
                readFailed={accounts === 'failed'}
                placeholder="Search account"
                hideBalance
              />
            )}
            {current !== null && rail.asset ? (
              <p className="text-meta text-tg-hint mt-1">
                {displayAsset(rail.asset)} · holds{' '}
                <span className={next !== null && amountN > 0 ? 'amt text-tg-text' : 'amt-key text-tg-text'}>{fmtAmount(current, rail.asset)}</span>
                {next !== null && amountN > 0 && <> → holds <span className="amt-key text-tg-text">{fmtAmount(next, rail.asset)}</span></>}
              </p>
            ) : rail.slug && accounts === 'waiting' && (
              <p className="text-meta text-tg-hint mt-1">Reading balance…</p>
            )}
          </Field>
        ) : (
          <>
            <Field label="Party">
              <Combobox
                value={slug}
                onChange={setSlug}
                options={partyOptions}
                more={rosterCut(parties.data)}
                placeholder="Search party"
                loading={slug.length > 0 && lookup === 'waiting'}
                emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
              />
              {!partyExists && lookup === 'known' && slug.trim() !== '' && (
                <p className="text-meta text-tg-hint mt-1">Pick a party from the list.</p>
              )}
              {current !== null ? (
                // The box speaks the fold (21 Oct 2026 audit, as on Record money): a chain-coded
                // chip ("USDT·TRC") made the line read "USDT·TRC · owes you 250,000" for a box
                // that holds bare USDT. `current` is read by the same fold.
                <BoxLines className="mt-1" asset={foldAsset(partyAsset)} before={current} after={next !== null && amountN > 0 ? next : undefined} />
              ) : partyExists && box === 'waiting' && (
                <p className="text-meta text-tg-hint mt-1">Reading balance…</p>
              )}
            </Field>
            {/* The asset before the amount (item 35): the desk's three as
                chips, the rest behind the dropdown — the registry's codes, as
                the thirteen-option select this replaced sent them. */}
            <Field label="Asset">
              <AssetPicker value={partyAsset} onChange={setPartyAsset} options={ASSETS} />
            </Field>
          </>
        )}
      </FieldRow>

      {/* WHICH WAY — above the amount, unpicked until the operator says. */}
      <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
        {WAYS.map((d) => (
          <button key={d.v} type="button" onClick={() => setDirection(d.v)} className={segmented(direction === d.v)}>
            {who === 'treasury' ? d.account : d.party}
          </button>
        ))}
      </div>

      <FieldRow>
        <Field label={asset ? `Amount in ${displayAsset(asset)}` : 'Amount'}>
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
      </FieldRow>

      <Field label="Reason" hint="required">
        <textarea className="input h-16" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Dust cleanup, wire shortfall, …" />
      </Field>

      {/* Deliberate friction on a screen that writes straight to the books.
          On the phone the line reads at the 13px content size and the box is
          a 16px thumb target (19 Sept 2026 phone pass); lg+ as before. */}
      <label className="flex items-start gap-2 text-body lg:text-meta max-lg:py-1">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5 max-lg:h-4 max-lg:w-4 max-lg:shrink-0" />
        <span className="text-tg-hint">This is a correction, not a way around re-booking</span>
      </label>

      {/* lg+: hairline separator + right-aligned, content-sized submit */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={submit.isPending || !direction || !amountOk(amount) || !targetPicked || (who === 'customer' && (!partyExists || lookup !== 'known')) || !reason || !confirm}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Booking…' : !direction ? 'Pick which way first' : 'Adjust balance'}
        </button>
      </div>

      {/* A deduped answer says so: the ledger already held this reference and
          nothing moved on this request (the retry safety the held reference
          gives). Otherwise the line says what the box now reads, with the
          reference, so the entry can be found. */}
      {submit.isSuccess && posted && <PostedLine posted={posted} />}
      {submit.isError && <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>}
    </div>
  );
}
